import {useState} from 'react'
import {getValidToken, sumAccountCountersForMaterialGroups} from '../utils/utils'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {MRT_API_BASEPATH} from '../ssr/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {BRAND_SHORT_LABEL} from '../brand'
import {useCustomPreference, useCustomPreferenceMarketless} from '../hooks/use-custom-preference'

import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

export const useHandleEmployeeBalance = () => {
    const {data: customer} = useCurrentCustomer()
    const [employeeError, setEmployeeError] = useState(null) // State to store errors
    const [employeeData, setEmployeeData] = useState(null) // State to store employee data
    const appOrigin = useAppOrigin(false)
    const {currency} = useCurrency()

    const employeeBalancePrefData = useCustomPreference('EmployeeProgram_Data') // Fetch employee program preferences
    const employeeBalanceRates = useCustomPreferenceMarketless('EmployeeProgram_Rates') // Fetch exchange rates

    // Function to fetch employee balance data
    const handleEmployeeBalance = async () => {
        try {
            // Get a valid access token for OMS API
            const tokenUrl = `${appOrigin}${MRT_API_BASEPATH}/get-auth-token-oms`
            const accessTokenKey = 'accessTokenOMS'
            const tokenExpirationKey = 'tokenExpirationOMS'
            var accessToken = await getValidToken(tokenUrl, accessTokenKey, tokenExpirationKey)
            if (!accessToken) {
                setEmployeeError('OMS Authentication failed')
                return
            }

            // Encode rates as a query parameter
            const ratesQueryParam = employeeBalanceRates
                ? encodeURIComponent(JSON.stringify(employeeBalanceRates))
                : ''

            // Fetch employee balance data from the OMS Employee API
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/get-employee-balance?customerId=${customer?.c_gigyaId}&brand=${BRAND_SHORT_LABEL}&sfAccountEmail=${customer?.email}&currency=${currency}&rates=${ratesQueryParam}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            )
            const result = await response.json()
            if (result.success) {
                setEmployeeData(result) // Store the result in state
                return result
            } else {
                setEmployeeError('Employee balance data retrieve failed: ' + result.error)
                console.error('Error during retrieving OMS data:', employeeError)
            }
        } catch (error) {
            setEmployeeError('Error during retrieving OMS data: ' + error.message)
            console.error('Error during retrieving OMS data:', employeeError)
        }
    }

    // Function to validate employee balance and product quantities
    const checkEmployeeBalanceAndProductQuantity = (basket, employeeData) => {
        if (!employeeData || !employeeBalancePrefData) {
            return {
                success: false,
                message: 'Employee data or Preference data are missing.',
            }
        }
        const totalAmountWithTax = employeeData.totalAmountWithTax || 0
        const balance = parseFloat(employeeBalancePrefData.balance)

        // Step 2: Count material groups from order summaries
        const orderSummaries = employeeData?.response?.records || []
        //general list where save all the final counting of the material groups
        const materialGroupCount = {}
        if (employeeBalancePrefData.products) {
            Object.keys(employeeBalancePrefData.products).forEach((materialCode) => {
                materialGroupCount[materialCode] = 0
            })
        }
        if (orderSummaries) {
            orderSummaries.forEach((orderSummary) => {
                const orderItemSummary = orderSummary?.OrderItemSummaries?.records || []
                orderItemSummary.forEach((item) => {
                    var materialGroup = item.ProductMaterialGroup__c
                    if (
                        materialGroupCount[materialGroup] !== undefined &&
                        materialGroupCount[materialGroup] >= 0
                    ) {
                        // If the material group is found in the preference data, add the quantity
                        materialGroupCount[materialGroup] += item.Quantity || 1
                    }
                })
            })
        }

        // Sum Account.Counter fields for each material group across all order summaries
        sumAccountCountersForMaterialGroups(materialGroupCount, orderSummaries)

        // Step 3: Count material groups and prices from the basket
        var totalAmountInBasket = 0
        var employeeDiscountInBasket = false
        var materialGroupInBasket = false
        basket.productItems.forEach((product) => {
            var hasEmployeeSalesDiscount = product?.priceAdjustments?.some(
                (adjustment) =>
                    adjustment.appliedDiscount &&
                    adjustment.appliedDiscount.priceBookId &&
                    adjustment.appliedDiscount.priceBookId.includes('employee-sales'),
            )
            if (hasEmployeeSalesDiscount) {
                employeeDiscountInBasket = true
                totalAmountInBasket += product.priceAfterItemDiscount || product.price
                const materialGroup = product.c_MaterialGroup
                if (
                    materialGroup &&
                    materialGroupCount[materialGroup] !== undefined &&
                    materialGroupCount[materialGroup] >= 0
                ) {
                    // If the material group is found in the basket, add the quantity
                    materialGroupInBasket = true
                    materialGroupCount[materialGroup] += product.quantity
                }
            }
        })

        // Step 4: Validate all product quantities and balance
        if (employeeDiscountInBasket) {
            const materialGroups = employeeBalancePrefData.products
            var allProductsAvailable = true
            const insufficientProducts = []
            if (materialGroupInBasket) {
                for (const materialCode in materialGroups) {
                    const availabilityMax = parseInt(materialGroups[materialCode])
                    const requestedCount = parseInt(materialGroupCount[materialCode] || 0)
                    if (requestedCount > availabilityMax) {
                        allProductsAvailable = false
                        insufficientProducts.push({
                            materialCode,
                            required: availabilityMax,
                            requested: requestedCount,
                        })
                    }
                }
            }
            var total = parseFloat(totalAmountWithTax) || 0
            if (total + totalAmountInBasket > balance) {
                return {
                    success: false,
                    message:
                        'The total amount of the discount price you are buying exceeds the available balance.',
                }
            }
            if (!allProductsAvailable && insufficientProducts.length > 0) {
                return {
                    success: false,
                    message: 'Insufficient products available.',
                    insufficientProducts,
                }
            }
        }
        return {
            success: true,
            message: 'Balance and product quantities are sufficient.',
        }
    }

    return {
        handleEmployeeBalance,
        checkEmployeeBalanceAndProductQuantity,
        employeeBalancePrefData,
        employeeError,
        employeeData,
    }
}
